import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { AlertTriangle, Check, Info, X } from './icons';

export type ToastKind = 'success' | 'error' | 'info';

export interface ToastOptions {
  title?: string;
  action?: { label: string; onClick: () => void };
  /** ms; default 4000, errors 8000; toasts with an action stay until dismissed */
  duration?: number;
}

interface ToastItem extends ToastOptions {
  id: number;
  kind: ToastKind;
  message: string;
}

export interface ToastApi {
  success: (message: string, options?: ToastOptions) => number;
  error: (message: string, options?: ToastOptions) => number;
  info: (message: string, options?: ToastOptions) => number;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const MAX_VISIBLE = 3;
const DEFAULT_DURATION: Record<ToastKind, number> = { success: 4000, info: 4000, error: 8000 };

const KIND_STYLES: Record<ToastKind, { icon: ReactNode; bar: string }> = {
  success: { icon: <Check className="h-5 w-5 text-green-500" />, bar: 'border-l-green-500' },
  error: { icon: <AlertTriangle className="h-5 w-5 text-red-500" />, bar: 'border-l-red-500' },
  info: { icon: <Info className="h-5 w-5 text-blue-500" />, bar: 'border-l-blue-500' },
};

let nextId = 1;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: number) => {
    const t = timers.current.get(id);
    if (t) clearTimeout(t);
    timers.current.delete(id);
    setToasts((prev) => prev.filter((x) => x.id !== id));
  }, []);

  const schedule = useCallback(
    (item: ToastItem) => {
      if (item.action && item.duration === undefined) return; // actionable toasts stay until the user decides, unless given a duration
      const ms = item.duration ?? DEFAULT_DURATION[item.kind];
      timers.current.set(
        item.id,
        setTimeout(() => dismiss(item.id), ms),
      );
    },
    [dismiss],
  );

  const push = useCallback(
    (kind: ToastKind, message: string, options: ToastOptions = {}) => {
      const item: ToastItem = { id: nextId++, kind, message, ...options };
      setToasts((prev) => {
        const next = [...prev, item];
        // Drop the oldest beyond the cap (and stop its timer).
        while (next.length > MAX_VISIBLE) {
          const dropped = next.shift()!;
          const t = timers.current.get(dropped.id);
          if (t) clearTimeout(t);
          timers.current.delete(dropped.id);
        }
        return next;
      });
      schedule(item);
      return item.id;
    },
    [schedule],
  );

  useEffect(() => {
    const map = timers.current;
    return () => {
      map.forEach((t) => clearTimeout(t));
      map.clear();
    };
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      success: (m, o) => push('success', m, o),
      error: (m, o) => push('error', m, o),
      info: (m, o) => push('info', m, o),
      dismiss,
    }),
    [push, dismiss],
  );

  const pause = (id: number) => {
    const t = timers.current.get(id);
    if (t) {
      clearTimeout(t);
      timers.current.delete(id);
    }
  };
  const resume = (item: ToastItem) => {
    if (!timers.current.has(item.id)) schedule(item);
  };

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        aria-live="polite"
        aria-atomic="false"
        className="pointer-events-none fixed bottom-4 right-4 z-[80] flex w-[min(24rem,calc(100vw-2rem))] flex-col gap-2"
      >
        {toasts.map((t) => {
          const style = KIND_STYLES[t.kind];
          return (
            <div
              key={t.id}
              role={t.kind === 'error' ? 'alert' : 'status'}
              onMouseEnter={() => pause(t.id)}
              onMouseLeave={() => resume(t)}
              className={cn(
                'pointer-events-auto flex items-start gap-3 rounded-md border border-l-4 border-gray-200 bg-white p-3 shadow-lg dark:border-gray-700 dark:bg-gray-800',
                style.bar,
              )}
            >
              <span className="mt-0.5 shrink-0">{style.icon}</span>
              <div className="min-w-0 flex-1 text-sm">
                {t.title && <p className="font-semibold text-gray-900 dark:text-gray-100">{t.title}</p>}
                <p className="break-words text-gray-700 dark:text-gray-300">{t.message}</p>
                {t.action && (
                  <button
                    type="button"
                    onClick={() => {
                      t.action!.onClick();
                      dismiss(t.id);
                    }}
                    className="mt-1 text-sm font-medium text-primary-600 hover:underline dark:text-primary-400"
                  >
                    {t.action.label}
                  </button>
                )}
              </div>
              <button
                type="button"
                onClick={() => dismiss(t.id)}
                aria-label="Dismiss notification"
                className="shrink-0 rounded p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

const noop: ToastApi = {
  success: (m) => {
    console.info('[toast]', m);
    return 0;
  },
  error: (m) => {
    console.error('[toast]', m);
    return 0;
  },
  info: (m) => {
    console.info('[toast]', m);
    return 0;
  },
  dismiss: () => undefined,
};

export function useToast(): ToastApi {
  return useContext(ToastContext) ?? noop;
}
